// Where a player sits at a game table, for everyone: the office (src/gomoku.ts re-exports it: placing and facing
// seated players, walking an agent to its own chair) and the page (public/gomoku.js walks you there). Pure, no DOM.
// Seat 1 (black / red / white in chess) sits on the table's left facing right, seat 2 on the right facing left. A
// map may place the chairs itself: points of class "chair" in the points layer with properties table (the table's id)
// and color (1 or 2), each at the spot where the otter's feet go; otherwise the chair is the stool the map draws
// next to the table (public/deck/entities.js Tables: feet one tile left / right of the table, half a tile down).
// Positions are an otter's (sprite top-left, as everywhere; feet at x + 0.5, y + 0.8).

/** feet tile free for walking (the movement rules' footAt) */
const footOk = (map, x, y) => { const lx = x - (map.x0 ?? 0), ly = y - (map.y0 ?? 0); return lx >= 0 && ly >= 0 && lx < map.width && ly < map.height && map.solid[ly][lx] !== '1'; };

/**
 * The chair of seat `color` (1 | 2) at `table` ({id, x, y} in tiles): {x, y} the otter's position, {dir, dir8} the
 * way it faces (4-way 0 down 1 right 2 up 3 left; 8-way = 2 * dir here), {tx, ty} the tile its feet are on. The spot
 * is moved outward (then up / down a little) if the map has a wall there; null if nothing near is free.
 */
export function seatSpot(map, table, color) {
  const left = color !== 2, dir = left ? 1 : 3;
  const chair = (map.chairs ?? []).find((c) => c.table === table.id && +c.color === (left ? 1 : 2));
  const feet = chair ? [[chair.x, chair.y]] : [];
  const out = left ? -1 : 1;
  for (const dx of [1, 1.5, 2]) for (const dy of [0.5, 1, 0]) feet.push([table.x + 0.5 + out * (dx + 0.5), table.y + dy]);
  for (const [fx, fy] of feet) {
    const tx = Math.floor(fx), ty = Math.floor(fy);
    if (footOk(map, tx, ty)) return { x: +(fx - 0.5).toFixed(2), y: +(fy - 0.8).toFixed(2), dir, dir8: dir * 2, dir16: dir * 4, tx, ty };
  }
  return null;
}

// ---- seats you sit on (椅子、长凳、工位、会议圆桌): the map's "seats" layer (deck/map.js MAP.seats). Walk up and press
// E, or click / tap the seat (public/sit.js); the office keeps who sits where (src/office/seats.ts, the peer's `seat`)
// and tells everyone, so others (and late joiners) see you sitting; moving off the spot stands you up. A page from
// before seats just sees an otter standing there.

/** Tiles from a seat's spot (the otter's position on it) to sit down, as the office has it. */
export const SEAT_REACH = 1.6;
/** Within this of the spot (tiles), an otter with a seat is sitting on it; further, it has stood up. */
export const SEATED = 0.3;
/** Working at a desk (工作中): voices on the open deck to and from you come at this volume (public/app.js loudness). */
export const DESK_HUSH = 0.6;

/** The seat with this id, or null. */
export function seatById(map, id) {
  if (!id || !map?.seats?.length) return null;
  // rebuilt when the seats change (a map edit: the page copies the edited map onto its MAP, the index with it)
  if (map._seatIxOf !== map.seats) { map._seatIx = new Map(map.seats.map((s) => [s.id, s])); map._seatIxOf = map.seats; }
  return map._seatIx.get(id) ?? null;
}
/** Tiles from an otter at (x, y) to seat s's spot. */
export const seatDist = (s, x, y) => Math.hypot(x - s.x, y - s.y);
/** The way a seat faces as {dir (4-way), dir8, dir16}, what a "pos" carries. */
export const seatFacing = (s) => ({ dir: Math.round(s.dir16 / 4) & 3, dir8: Math.round(s.dir16 / 2) & 7, dir16: s.dir16 });
/** The nearest seat to (x, y) within `reach` that isn't taken (taken(id): someone else sits there), or null. */
export function nearestSeat(map, x, y, taken = () => false, reach = SEAT_REACH) {
  let best = null, bd = reach;
  for (const s of map?.seats ?? []) { const d = seatDist(s, x, y); if (d <= bd && !taken(s.id)) { bd = d; best = s; } }
  return best;
}
/** The seat under a point of the map (wx, wy in tiles, e.g. a click): within half a tile of where its sitter is drawn. */
export function seatAtPoint(map, wx, wy) {
  let best = null, bd = 0.6;
  for (const s of map?.seats ?? []) { const d = Math.hypot(wx - s.fx, wy - (s.fy - 0.4 - s.lift / 16)); if (d < bd) { bd = d; best = s; } }
  return best;
}
/** The seat p ({seat, x, y}) is sitting on: it has one and is on its spot; else null. */
export function sittingOn(map, p) {
  const s = p?.seat ? seatById(map, p.seat) : null;
  return s && !p.boat && seatDist(s, p.x, p.y) <= SEATED ? s : null;
}
/** The voice circle p is in: the meeting table (its group) while sitting at one; else null. */
export function circleOf(map, p) { const s = sittingOn(map, p); return s?.kind === 'meeting' ? s.group : null; }
/** Working at a desk (工作中): sitting at one. */
export const atDesk = (map, p) => sittingOn(map, p)?.kind === 'desk';

// ---- 我的工位 (personal desks): src/office/desks.ts keeps who owns which desk, public/desks.js shows it.
/** What an owner may put on their desk: small things of the map's own art (the "sprites" of map-fronts.png, drawn by
 *  tools/tiled/draw-map.mjs from assets/map/map-art.mjs; no new art), with their names' catalog keys (public/locales; the
 *  office imports this file too, so no /i18n.js here: public/desks.js words them). */
export const DESK_DECOR = [['plant', 'desks.decor.plant'], ['lamp', 'desks.decor.lamp'], ['mug', 'desks.decor.mug'], ['flowers', 'desks.decor.flowers'], ['easel', 'desks.decor.easel'], ['chest', 'desks.decor.chest'], ['barrel', 'desks.decor.barrel'], ['ring', 'desks.decor.ring']];
/** Desks decorated before the decorations were things of the map (icons then): the old ids still stored, and what
 *  each one is now (null: nothing like it; it's dropped). */
export const DESK_DECOR_OLD = { bulb: 'lamp', coral: 'plant', trophy: 'chest', crown: 'chest', star: null, heart: null, crystal: null, fish: null };
/** A stored decorations list (ids, comma separated) as decorations now: old ids mapped, unknown ones and repeats dropped. */
export function deskDecorOf(stored) {
  const ids = new Set(DESK_DECOR.map(([id]) => id)), out = [];
  for (const d of String(stored ?? '').split(',')) {
    const id = Object.hasOwn(DESK_DECOR_OLD, d) ? DESK_DECOR_OLD[d] : d;
    if (id && ids.has(id) && !out.includes(id)) out.push(id);
  }
  return out.slice(0, DESK_DECOR_MAX);
}
/** At most this many at once. */
export const DESK_DECOR_MAX = 3;
